/**
 * Priority rules for home template selection (Worker inject vs admin preview).
 *
 * **Works for everyone (production):**
 * 1. Live edge inject (cloudflare_geo / cookies / manual_override) always wins
 * 2. Sticky admin_preview / test_country are IGNORED unless URL is explicit
 *    (`?preview=` or `?country=`) or geo_sim is active
 * 3. Explicit URL preview still forces DualCTA/US for admin audit
 *
 * Unit tests: scripts/validate-ar-exclusive-home-template-selection.ts
 */

import { getWorkerTemplateConfigForCountry } from "@/config/workerCountryTemplateMap";
import type { ServerTemplateConfig } from "@/lib/serverTemplateDetection";

/** Edge inject with real geo (or cookie CTA). */
export function isEdgeCtaConfig(
  cfg: ServerTemplateConfig | null | undefined,
): boolean {
  if (!cfg) return false;
  const m = cfg.detectionMethod || "";
  return (
    m === "cloudflare_geo" ||
    m === "manual_override" ||
    (m === "cookies_fallback" &&
      (cfg.showCommunity === true ||
        cfg.hideCta === true ||
        cfg.showBirthday === true ||
        cfg.showDualVideo === true))
  );
}

/** Any live edge geo from Worker/cookies (not build_default / client map). */
export function isLiveEdgeConfig(
  cfg: ServerTemplateConfig | null | undefined,
): boolean {
  if (!cfg) return false;
  const m = cfg.detectionMethod || "";
  return (
    m === "cloudflare_geo" ||
    m === "manual_override" ||
    m === "cookies_fallback"
  );
}

/**
 * Live Argentina from Worker/cookies. Sticky non-AR must never demote this
 * to DualCTAVideoLanding for real visitors.
 */
export function isLiveArEdge(
  cfg: ServerTemplateConfig | null | undefined,
): boolean {
  if (!cfg) return false;
  if (cfg.country !== "AR") return false;
  return isLiveEdgeConfig(cfg);
}

/** Edge AR with Exclusive (or other geo CTA) flags. */
export function isLiveArExclusiveEdge(
  cfg: ServerTemplateConfig | null | undefined,
): boolean {
  if (!isLiveArEdge(cfg)) return false;
  return (
    cfg!.showCommunity === true ||
    cfg!.hideCta === true ||
    cfg!.showBirthday === true ||
    cfg!.showDualVideo === true
  );
}

/**
 * Client country map (?country / explicit preview) without wiping edge CTA
 * when the mapped country matches live inject.
 */
export function clientMapWithEdgeCta(
  code: string,
  edge: ServerTemplateConfig | null,
): ServerTemplateConfig {
  const mapped = getWorkerTemplateConfigForCountry(code);
  if (edge && isLiveEdgeConfig(edge) && edge.country === code) {
    return {
      ...mapped,
      ...edge,
      country: code,
      templateName: edge.templateName || mapped.templateName,
    };
  }
  return mapped;
}

export type TemplateSelectionIntent = {
  geoSimActive?: boolean;
  /** From URL ?preview=international|argentina (explicit). */
  urlPreviewCountry?: string | null;
  /** From URL ?country=XX (explicit). */
  urlCountryOverride?: string | null;
  /**
   * sessionStorage admin_preview_country — only applied when URL is explicit
   * or geo_sim; otherwise ignored so real visitors are never demoted.
   */
  stickyPreviewCountry?: string | null;
  /** localStorage test_country — same rule as sticky. */
  testCountry?: string | null;
};

function normIso2(raw: string | null | undefined): string | null {
  if (!raw || typeof raw !== "string") return null;
  const t = raw.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(t)) return null;
  return t;
}

/**
 * Resolve config for first paint (useState initializer).
 *
 * Sticky/test_country alone NEVER override live edge — every real visitor
 * gets Worker geo. Only explicit URL or geo_sim may force another country.
 */
export function resolveInitialTemplateConfig(
  edge: ServerTemplateConfig | null,
  intent: TemplateSelectionIntent = {},
): ServerTemplateConfig {
  const geoSim = !!intent.geoSimActive;

  if (geoSim && edge) {
    return edge;
  }

  // Explicit URL always wins (shareable / admin preview).
  const explicit =
    normIso2(intent.urlCountryOverride) ||
    normIso2(intent.urlPreviewCountry);
  if (explicit) {
    return clientMapWithEdgeCta(explicit, edge);
  }

  // Live edge geo — source of truth for everyone (AR Exclusive + intl DualCTA).
  if (edge && isLiveEdgeConfig(edge)) {
    return edge;
  }

  // No sticky demotion: leftover admin_preview / test_country are ignored
  // without URL. Real visitors must never load DualCTA from stale storage.
  // (Admin: use ?preview=international or ?country=US.)

  if (edge) return edge;

  return {
    country: "UNKNOWN",
    templateName: "BaseTemplate",
    bioLayout: null,
    language: "en-US",
    province: null,
    cfCountry: "UNKNOWN",
    timezone: "",
    city: "",
    detectionMethod: "fallback",
  };
}

/**
 * After mount: only apply sticky when URL is still explicit.
 * Otherwise clear demoting sticky if live edge is AR (cleanup admin leftover).
 */
export function resolveStickyPreviewAgainstEdge(
  stickyPreviewCountry: string | null | undefined,
  edge: ServerTemplateConfig | null,
  options?: { urlExplicit?: boolean },
): {
  config: ServerTemplateConfig | null;
  clearSticky: boolean;
} {
  const sticky = normIso2(stickyPreviewCountry);
  if (!sticky) return { config: null, clearSticky: false };

  // Without explicit URL, never apply sticky — prefer edge and clear leftover.
  if (!options?.urlExplicit) {
    if (isLiveEdgeConfig(edge)) {
      return {
        config: edge!,
        // Clear sticky US leftover so banner disappears for real AR sessions
        clearSticky: sticky !== edge!.country,
      };
    }
    return { config: null, clearSticky: sticky !== "AR" && isLiveArEdge(edge) };
  }

  // Explicit URL path: merge edge CTA when same country
  if (edge && isLiveEdgeConfig(edge) && edge.country === sticky) {
    return { config: edge, clearSticky: false };
  }
  return { config: clientMapWithEdgeCta(sticky, edge), clearSticky: false };
}

/**
 * Home template name when country drives AR vs intl.
 */
export function homeTemplateFromSelection(
  config: ServerTemplateConfig,
  internationalHomeTemplate: string | null | undefined,
  internationalHomeTemplateByCountry: Record<string, string> | null | undefined,
): string {
  const isAr = config.country === "AR";
  if (isAr) {
    const arRaw = internationalHomeTemplateByCountry?.["AR"];
    if (typeof arRaw === "string" && arRaw.trim()) {
      return arRaw.trim();
    }
    return config.templateName || "BaseTemplate";
  }
  const by =
    (config.country &&
      internationalHomeTemplateByCountry?.[config.country]) ||
    internationalHomeTemplate ||
    "DualCTAVideoLanding";
  return by;
}
